import { useState, useEffect } from "react";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import {
  getAuth as getAuthFromAuthModule,
  createUserWithEmailAndPassword,
  signOut as signOutSecondaryAuth,
} from "firebase/auth";
import { initializeApp, deleteApp } from "firebase/app";
import { db } from "../../services/firebase";
import { addLog } from "../../utils/audit";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../services/firebase";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SANDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export default function UserAdministration() {
  const [user] = useAuthState(auth);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "student",
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "" });

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
      const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(docs);
      setFilteredUsers(docs);
    });
    return () => unsubscribe();
  }, []);

  // Filter users based on search and role
  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter !== "all") {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter]);

  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const handleAddUser = async () => {
    const { name, email, password, role } = newUser;
    if (!name.trim() || !email.trim() || !password.trim() || !role.trim()) {
      alert("⚠️ All fields are required.");
      return;
    }
    if (!isValidEmail(email)) {
      alert("⚠️ Please enter a valid email address.");
      return;
    }
    if (password.length < 6) {
      alert("⚠️ Password must be at least 6 characters long.");
      return;
    }

    setLoading(true);
    let secondaryApp = null;

    try {
      const appName = `secondary-${Date.now()}`;
      secondaryApp = initializeApp(firebaseConfig, appName);
      const secondaryAuth = getAuthFromAuthModule(secondaryApp);
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        email,
        password
      );

      const user = userCredential.user;
      await setDoc(doc(db, "users", user.uid), {
        name: name.trim(),
        email: email.trim(),
        role: role.trim(),
        createdAt: new Date(),
        createdBy: user?.uid,
        isActive: true,
      });

      await addLog(user?.uid, `Added user: ${name} (${role})`, { uid: user.uid, role });
      setNewUser({ name: "", email: "", password: "", role: "student" });
      alert("✅ User added successfully!");
    } catch (err) {
      console.error("Error adding user:", err);
      alert("❌ Failed to add user: " + (err?.message || err));
    } finally {
      try {
        if (secondaryApp) {
          const secondaryAuth = getAuthFromAuthModule(secondaryApp);
          await signOutSecondaryAuth(secondaryAuth).catch(() => {});
          await deleteApp(secondaryApp).catch(() => {});
        }
      } catch {}
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;

    const { name, email, role } = editForm;
    if (!name.trim() || !email.trim() || !role.trim()) {
      alert("⚠️ All fields are required.");
      return;
    }
    if (!isValidEmail(email)) {
      alert("⚠️ Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, "users", editingUser.id), {
        name: name.trim(),
        email: email.trim(),
        role: role.trim(),
        lastUpdated: new Date(),
        updatedBy: user?.uid,
      });

      await addLog(user?.uid, `Updated user: ${name} (${role})`, {
        uid: editingUser.id,
        oldRole: editingUser.role,
        newRole: role
      });

      setEditingUser(null);
      setEditForm({ name: "", email: "", role: "" });
      alert("✅ User updated successfully!");
    } catch (err) {
      console.error("Error updating user:", err);
      alert("❌ Failed to update user: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`Delete user ${name || "Unnamed"}? This action cannot be undone.`)) return;

    try {
      await deleteDoc(doc(db, "users", id));
      await addLog(user?.uid, `Deleted user: ${name}`, { uid: id });
      alert(`✅ User ${name || "Unnamed"} deleted`);
    } catch (err) {
      console.error("Error deleting user:", err);
      alert("❌ Failed to delete user: " + err.message);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus, userName) => {
    try {
      await updateDoc(doc(db, "users", userId), {
        isActive: !currentStatus,
        lastUpdated: new Date(),
        updatedBy: user?.uid,
      });

      await addLog(user?.uid, `${!currentStatus ? "Activated" : "Deactivated"} user: ${userName}`, {
        uid: userId,
        newStatus: !currentStatus
      });

      alert(`✅ User ${!currentStatus ? "activated" : "deactivated"} successfully!`);
    } catch (err) {
      console.error("Error toggling user status:", err);
      alert("❌ Failed to update user status: " + err.message);
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      role: user.role || "student"
    });
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({ name: "", email: "", role: "" });
  };

  // User Statistics
  const userStats = {
    total: users.length,
    students: users.filter(u => u.role === "student").length,
    admins: users.filter(u => u.role === "admin").length,
    technicians: users.filter(u => u.role === "technician").length,
    active: users.filter(u => u.isActive !== false).length,
    inactive: users.filter(u => u.isActive === false).length,
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-100 text-red-800 border-red-200";
      case "technician": return "bg-blue-100 text-blue-800 border-blue-200";
      case "student": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Administration</h2>
          <p className="text-gray-600 mt-1">Manage users with role-based access control</p>
        </div>
        <div className="text-sm text-gray-500">
          Total Users: {users.length}
        </div>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-2xl font-bold text-blue-600">{userStats.total}</div>
          <div className="text-sm text-blue-800">Total Users</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-2xl font-bold text-green-600">{userStats.students}</div>
          <div className="text-sm text-green-800">Students</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="text-2xl font-bold text-red-600">{userStats.admins}</div>
          <div className="text-sm text-red-800">Admins</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="text-2xl font-bold text-purple-600">{userStats.technicians}</div>
          <div className="text-sm text-purple-800">Technicians</div>
        </div>
        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
          <div className="text-2xl font-bold text-emerald-600">{userStats.active}</div>
          <div className="text-sm text-emerald-800">Active</div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-gray-600">{userStats.inactive}</div>
          <div className="text-sm text-gray-800">Inactive</div>
        </div>
      </div>

      {/* Add New User Form */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Add New User</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <input
            type="text"
            placeholder="Full Name"
            value={newUser.name}
            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <input
            type="email"
            placeholder="Email"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <input
            type="password"
            placeholder="Password"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <select
            value={newUser.role}
            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="student">Student</option>
            <option value="technician">Technician</option>
            <option value="admin">Admin</option>
          </select>
          <button
            onClick={handleAddUser}
            disabled={loading}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Adding..." : "Add User"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Roles</option>
              <option value="student">Students</option>
              <option value="technician">Technicians</option>
              <option value="admin">Admins</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-500">
              Showing: {filteredUsers.length} of {users.length} users
            </div>
          </div>
        </div>
      </div>

      {/* Edit User Modal/Form */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit User</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Full Name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="email"
                placeholder="Email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="student">Student</option>
                <option value="technician">Technician</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleEditUser}
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? "Updating..." : "Update"}
              </button>
              <button
                onClick={cancelEdit}
                className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="space-y-4">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          filteredUsers.map((u) => (
            <div key={u.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                <div className="flex items-start space-x-4 flex-1 mb-4 md:mb-0">
                  <img
                    src={u.profilePicture || "/adminnew.jpg"}
                    alt="Profile"
                    className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-gray-300 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center space-x-2 md:space-x-3 mb-2">
                      <h3 className="font-semibold text-base md:text-lg text-gray-900">{u.name}</h3>
                      <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium border ${getRoleColor(u.role)}`}>
                        {u.role?.charAt(0).toUpperCase() + u.role?.slice(1)}
                      </span>
                      {u.isActive === false && (
                        <span className="px-2 md:px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium border border-gray-200">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-1 text-sm md:text-base break-all">{u.email}</p>
                    <div className="text-xs md:text-sm text-gray-500">
                      Created: {u.createdAt ? new Date(u.createdAt.toDate?.() || u.createdAt).toLocaleDateString() : "N/A"}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 md:space-x-2 md:gap-0">
                  <button
                    onClick={() => startEdit(u)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex-shrink-0"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleUserStatus(u.id, u.isActive !== false, u.name)}
                    className={`px-3 py-1 text-white text-sm rounded flex-shrink-0 ${
                      u.isActive === false ? "bg-green-600 hover:bg-green-700" : "bg-yellow-600 hover:bg-yellow-700"
                    }`}
                  >
                    {u.isActive === false ? "Activate" : "Deactivate"}
                  </button>
                  <button
                    onClick={() => handleDeleteUser(u.id, u.name)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex-shrink-0"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
